import { useState } from 'react';
import { Search, Plus, Trash2, Plug, ShieldCheck, Check } from 'lucide-react';
import {
  Button, Card, CardHeader, Checkbox, DateInput, DayGrid, DashTile, DashTileGrid, Drawer, DropZone, EmptyState,
  FeedList, FeedRow, Field, IconButton, Input, Kbd, KeyBadge, Kpi, KpiStrip, PanelHeader, PaperFrame,
  Pill, ProcessSteps, SegmentedControl, Select, Spinner, SplitPanel, StatusDot, Switch, TableFooter,
  TableHead, TableRow, Tag, Textarea, Toolbar, ToolbarDivider, Tooltip, printA4, useToast,
} from '@os/ui';
import { Page } from '../../desktop/Page';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const dayCols = Array.from({ length: 7 }, (_, i) => ({ key: String(i + 1), label: String(i + 1), muted: i === 5, weekday: WEEKDAYS[i] }));
const dayRow = {
  id: 'a',
  label: 'Ada',
  sub: 'Full-time · A$100 per day',
  tally: '5 worked · 1 unpaid',
  cells: [
    { key: '1', title: 'work', tone: 'ok' as const, label: 'P', today: true },
    { key: '2', title: 'paid', tone: 'warn' as const, label: 'L' },
    { key: '3' },
    { key: '4', title: 'unpaid', tone: 'danger' as const, label: 'U' },
    { key: '5' },
    { key: '6', tone: 'off' as const, disabled: true, label: '—' },
    { key: '7', label: '·' },
  ],
};

/** Living catalogue of @os/ui `src/ui` — every primitive in its states. Stands in for Storybook (docs/UI_LIBRARY.md). */
export default function Gallery() {
  const toast = useToast((s) => s.show);
  const [seg, setSeg] = useState<'all' | 'open' | 'done'>('all');
  const [drawer, setDrawer] = useState(false);
  const [on, setOn] = useState(true);
  const [splitKey, setSplitKey] = useState<string | null>(null);
  const [splitMax, setSplitMax] = useState(false);
  const [rail, setRail] = useState(false);
  const rows = [{ id: '1', name: 'Alpha', status: 'ok' }, { id: '2', name: 'Beta', status: 'warn' }, { id: '3', name: 'Gamma', status: 'danger' }] as const;
  const selected = rows.find((r) => r.id === splitKey);
  return (
    <>
      <Page
        title="UI gallery"
        subtitle="Every named export from packages/ui/src/ui — the living catalogue until Storybook lands"
        actions={(
          <>
            <Button onClick={() => toast('Hello from Toast')}>Toast</Button>
            <Button variant="danger-outline" onClick={() => toast('Something failed', 'danger')}>Danger toast</Button>
          </>
        )}
      >
        <p className="meta" data-gallery-index style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: 0 }}>
          {['Button', 'IconButton', 'Pill', 'Tag', 'Card', 'CardHeader', 'Kpi', 'DashTile', 'DashTileGrid', 'Input', 'DateInput', 'Select', 'Textarea', 'Field', 'Checkbox', 'Switch', 'SegmentedControl', 'Drawer', 'SplitPanel', 'PanelHeader', 'PaperFrame', 'printA4', 'DayGrid', 'FeedList', 'FeedRow', 'TableHead', 'TableRow', 'TableFooter', 'EmptyState', 'Kbd', 'KeyBadge', 'Tooltip', 'Toolbar', 'ToolbarDivider', 'Spinner', 'ToastHost', 'useToast'].map((n) => <Tag key={n}>{n}</Tag>)}
        </p>
        <KpiStrip columns={4}>
          <Kpi label="Users" value="12" />
          <Kpi label="Apps" value="3" sub="+1 this week" />
          <Kpi label="Errors" value="0" tone="muted" size={24} />
          <Kpi label="Fill" value="64%" bar={64} />
        </KpiStrip>
        <KpiStrip square><Kpi label="Hours" value="171.0 h" sub="This week" /><Kpi label="To raise" value="A$19,124" /><Kpi label="Outstanding" value="A$11,878" tone="danger" /><Kpi label="Cash" value="A$2,579" /></KpiStrip>
        <DashTileGrid>
          <DashTile label="Invoice to raise" value="A$19,123.50" sub="INV-2026-036 · due 20 Sep 2026" selected onList={() => toast('List')} onPreview={() => toast('Preview')} onAdd={() => toast('Edit')} addLabel="Edit lines" previewActive />
          <DashTile label="Record payment" value="A$11,877.80" sub="INV-2026-034 · due 6 Sep 2026" tone="danger" onList={() => toast('List')} onPreview={() => toast('Preview')} onAdd={() => toast('Receipt')} />
          <DashTile variant="bar" label="Cash on hand" value="A$2,579.08" sub="as at today" onList={() => toast('List')} onPreview={() => toast('Preview')} onMinimise={() => toast('Min')} onMaximise={() => toast('Max')} onClose={() => toast('Close')} />
        </DashTileGrid>
        <Card>
          <CardHeader
            title="SplitPanel + PanelHeader + PaperFrame"
            action={(
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="inline" onClick={() => setRail((v) => !v)}>{rail ? 'Default split' : 'Rail'}</Button>
                <Button size="inline" onClick={() => printA4()}>printA4</Button>
              </div>
            )}
          />
          <SplitPanel
            open={!!splitKey}
            panelKey={splitKey ?? 'closed'}
            panelMax={360}
            panelFrac={0.45}
            maximised={splitMax}
            rail={rail}
            railMax={220}
            list={rows.map((r) => (
              <TableRow key={r.id} cols="1fr 80px" selected={r.id === splitKey} onClick={() => { setSplitKey(r.id); setSplitMax(false); }}>
                <span>{r.name}</span>
                <span style={{ textAlign: 'right' }}><Pill tone={r.status}>{r.status}</Pill></span>
              </TableRow>
            ))}
            panelTop={(
              <PanelHeader
                kicker="Document"
                title={selected?.name ?? '—'}
                subtitle="Krost · net 14"
                maximised={splitMax}
                onMaximise={() => setSplitMax(true)}
                onMinimise={() => setSplitMax(false)}
                onClose={() => { setSplitKey(null); setSplitMax(false); }}
                actions={<Button size="inline">Download</Button>}
              />
            )}
            panel={<PaperFrame width={420} compact fileName="inv-2026-036.pdf"><div className="kicker">Tax invoice</div><div style={{ font: '600 22px var(--font-heading)', margin: '8px 0 16px' }}>{selected?.name ?? 'Document'}</div><div className="meta">White sheet on grey — used by every app’s right panel. ToastHost lives in the desktop shell.</div></PaperFrame>}
            style={{ height: 360 }}
          />
        </Card>
        <div className="pg-tiles" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))' }}>
        <Card>
          <CardHeader title="Button, IconButton, Spinner, Kbd, KeyBadge" />
          <div className="pg-card-body">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="primary">Primary</Button><Button>Secondary</Button><Button variant="ghost">Ghost</Button><Button variant="danger-outline">Danger outline</Button><Button variant="danger">Danger</Button><Button disabled>Disabled</Button><Button loading>Loading</Button>
              <Button size="header">Header</Button><Button size="toolbar">Toolbar</Button><Button size="inline">Inline</Button><Button size="row">Row</Button><Button size="form">Form</Button>
              <IconButton label="Add" icon={<Plus size={15} />} /><IconButton label="Active" active icon={<Plus size={15} />} /><IconButton label="Delete" tone="danger" icon={<Trash2 size={15} />} /><IconButton label="Disabled" disabled icon={<Plus size={15} />} />
              <Spinner /> <Kbd>Esc</Kbd> <KeyBadge>K</KeyBadge>
            </div>
          </div>
        </Card>
        <Card>
          <CardHeader title="Pill, Tag, Tooltip" />
          <div className="pg-card-body">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Pill tone="ok">Confirmed</Pill><Pill tone="warn">Needs answer</Pill><Pill tone="danger">Missing</Pill><Pill>Outline</Pill><Pill tone="muted">Muted</Pill><Pill small>Small</Pill>
              <Tag>Tag</Tag><StatusDot tone="ok" /><StatusDot tone="warn" /><StatusDot tone="danger" /><StatusDot tone="off" />
              <Tooltip label="Tooltip text"><span className="meta">hover me</span></Tooltip>
            </div>
          </div>
        </Card>
        <Card>
          <CardHeader title="Input, DateInput, Select, Textarea, Field, Checkbox, Switch, SegmentedControl" />
          <div className="pg-card-body">
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(3, 1fr)' }} data-cols>
              <Field label="Text"><Input placeholder="Placeholder" /></Field>
              <Field label="Compact"><Input compact placeholder="Compact" /></Field>
              <Field label="Date"><DateInput defaultValue="2026-09-08" /></Field>
              <Field label="Select" hint="Hint text"><Select><option>One</option><option>Two</option></Select></Field>
              <Field label="Textarea" style={{ gridColumn: 'span 2' }}><Textarea rows={3} placeholder="Notes…" /></Field>
              <Checkbox label="Checkbox" hint="Optional hint" defaultChecked /><Switch checked={on} onChange={setOn} label="Switch" /><Switch checked={on} onChange={setOn} size="sm" label="Small switch" />
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SegmentedControl value={seg} onChange={setSeg} segments={[{ value: 'all', label: 'All' }, { value: 'open', label: 'Open', count: 3 }, { value: 'done', label: 'Done' }]} showLabels />
                <SegmentedControl value={seg} onChange={setSeg} variant="tabs" segments={[{ value: 'all', label: 'All' }, { value: 'open', label: 'Open', count: 3 }, { value: 'done', label: 'Done' }]} />
                <SegmentedControl value={seg} onChange={setSeg} variant="chips" showLabels segments={[{ value: 'all', label: 'All' }, { value: 'open', label: 'Open' }, { value: 'done', label: 'Done' }]} />
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <CardHeader title="Toolbar, TableHead, TableRow, TableFooter" />
          <Toolbar><Select compact aria-label="Period"><option>This month</option></Select><ToolbarDivider /><Input compact placeholder="Search…" aria-label="Search" style={{ width: 200 }} /><Search size={14} style={{ color: 'var(--text-3)' }} /></Toolbar>
          <TableHead cols="1fr 120px" columns={[{ label: 'Name' }, { label: 'Status', align: 'right' }]} />
          {rows.map((r) => <TableRow key={r.id} cols="1fr 120px"><span>{r.name}</span><span style={{ textAlign: 'right' }}><Pill tone={r.status}>{r.status}</Pill></span></TableRow>)}
          <TableFooter><span>3 rows</span><span>TableFooter</span></TableFooter>
        </Card>
        <Card>
          <CardHeader title="Process steps" />
          <div className="pg-card-body">
            <ProcessSteps current={1} steps={[{ title: 'Connect', sub: 'Link the account', icon: <Plug size={16} /> }, { title: 'Verify', sub: 'Confirm access', icon: <ShieldCheck size={16} /> }, { title: 'Finish', sub: 'Done', icon: <Check size={16} /> }]} />
          </div>
        </Card>
        <Card>
          <CardHeader title="DayGrid bands" />
          <div className="pg-card-body" style={{ paddingBottom: 16 }}>
            <DayGrid
              layout="bands"
              title="Attendance"
              legend="P worked · L paid leave · U unpaid"
              footerLeft="Fridays off"
              footerRight="A$100 unpaid"
              columns={dayCols}
              rows={[dayRow]}
            />
          </div>
        </Card>
        <Card>
          <CardHeader title="DayGrid table" />
          <div className="pg-card-body" style={{ paddingBottom: 16, overflowX: 'auto' }}>
            <DayGrid layout="table" columns={dayCols} rows={[dayRow]} onCellClick={(id, cell) => toast(`${id} · ${cell.key}`)} />
          </div>
        </Card>
        <Card>
          <CardHeader title="FeedList, FeedRow" />
          <div className="pg-card-body" style={{ padding: 0, display: 'grid', gap: 12 }}>
            <FeedList title="Invoices" action={<Button size="row">New</Button>}>
              <FeedRow title="INV-2026-036" meta="31 Aug – 06 Sept" amount="A$16,354.25" selected onClick={() => toast('Selected')} />
              <FeedRow title="INV-2026-035" meta="24 Aug – 30 Aug" amount="A$12,737.45" action={<Button size="row">Open</Button>} onClick={() => toast('Selected')} />
            </FeedList>
            <FeedList title="Payments" empty="No payments yet" />
          </div>
        </Card>
        </div>
        <EmptyState dashed title="Nothing here" body="Try the drawer. ToastHost is mounted on the desktop shell." action={<Button variant="primary" onClick={() => setDrawer(true)}>Open drawer</Button>} />
        <EmptyState compact title="Compact empty" body="EmptyState compact — used inside lists." />
        <DropZone title="Drop receipts, invoices or photos" body="PDF or photo · OCR reads supplier, date, total and GST" onFiles={() => toast('Dropped')} />
        <DropZone compact title="Drop files to upload" body="Duplicates skipped" onFiles={() => toast('Dropped')} />
      </Page>
      <Drawer open={drawer} title="Drawer" onClose={() => setDrawer(false)} footer={<><Button size="form" onClick={() => setDrawer(false)}>Cancel</Button><Button variant="primary" size="form" onClick={() => setDrawer(false)}>Save</Button></>}>
        <div className="meta">460–520px right-side overlay with backdrop, sticky header and footer.</div>
      </Drawer>
    </>
  );
}
